function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { useContext } from "react";
import PropTypes from "prop-types";
import HiddenCheckableInputStyle from "./hidden-checkable-input.style";
import { InputContext, InputGroupContext } from "../input-behaviour";
const HiddenCheckableInput = /*#__PURE__*/React.forwardRef(({
  ariaDescribedBy,
  ariaLabelledBy,
  name,
  checked,
  type,
  value,
  onChange,
  autoFocus,
  role,
  validationIconId,
  ...props
}, ref) => {
  const {
    onBlur,
    onFocus,
    onMouseEnter,
    onMouseLeave,
    hasFocus,
    hasMouseOver
  } = useContext(InputContext);
  const {
    onBlur: onBlurGroup,
    onFocus: onFocusGroup,
    onMouseEnter: onMouseEnterGroup,
    onMouseLeave: onMouseLeaveGroup,
    hasFocus: hasGroupFocus,
    hasMouseOver: hasGroupMouseOver
  } = useContext(InputGroupContext);
  const handleFocus = ev => {
    if (props.onFocus) props.onFocus(ev);
    if (onFocus) onFocus();
    if (onFocusGroup) onFocusGroup();
  };
  const handleBlur = ev => {
    if (props.onBlur) props.onBlur(ev);
    if (onBlur) onBlur();
    if (onBlurGroup) onBlurGroup();
  };
  const handleMouseEnter = ev => {
    if (props.onMouseEnter) props.onMouseEnter(ev);
    if (onMouseEnter) onMouseEnter();
    if (onMouseEnterGroup) onMouseEnterGroup();
  };
  const handleMouseLeave = ev => {
    if (props.onMouseLeave) props.onMouseLeave(ev);
    if (onMouseLeave) onMouseLeave();
    if (onMouseLeaveGroup) onMouseLeaveGroup();
  };
  const hasValidationPart = (hasFocus || hasGroupFocus || hasMouseOver || hasGroupMouseOver) && validationIconId;
  const descriptionList = ariaDescribedBy ? [ariaDescribedBy] : [];
  if (hasValidationPart) {
    descriptionList.push(validationIconId);
  }
  const combinedDescription = descriptionList.length ? descriptionList.filter(Boolean).join(" ") : undefined;
  return /*#__PURE__*/React.createElement(HiddenCheckableInputStyle, _extends({
    "aria-describedby": combinedDescription,
    "aria-labelledby": ariaLabelledBy,
    autoFocus: autoFocus,
    "data-has-autofocus": autoFocus ? true : undefined,
    "aria-checked": checked,
    checked: checked,
    name: name,
    role: role || type,
    type: type,
    value: value
  }, props, {
    onFocus: handleFocus,
    onBlur: handleBlur,
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
    onChange: onChange,
    ref: ref
  }));
});
HiddenCheckableInput.propTypes = {
  "about": PropTypes.string,
  "accept": PropTypes.string,
  "accessKey": PropTypes.string,
  "alt": PropTypes.string,
  "aria-activedescendant": PropTypes.string,
  "aria-atomic": PropTypes.oneOfType([PropTypes.oneOf(["false", "true"]), PropTypes.bool]),
  "aria-autocomplete": PropTypes.oneOf(["both", "inline", "list", "none"]),
  "aria-busy": PropTypes.oneOfType([PropTypes.oneOf(["false", "true"]), PropTypes.bool]),
  "aria-checked": PropTypes.oneOfType([PropTypes.oneOf(["false", "mixed", "true"]), PropTypes.bool]),
  "aria-colcount": PropTypes.number,
  "aria-colindex": PropTypes.number,
  "aria-colspan": PropTypes.number,
  "aria-controls": PropTypes.string,
  "aria-current": PropTypes.oneOfType([PropTypes.oneOf(["date", "false", "location", "page", "step", "time", "true"]), PropTypes.bool]),
  "aria-describedby": PropTypes.string,
  "aria-details": PropTypes.string,
  "aria-disabled": PropTypes.oneOfType([PropTypes.oneOf(["false", "true"]), PropTypes.bool]),
  "aria-dropeffect": PropTypes.oneOf(["copy", "execute", "link", "move", "none", "popup"]),
  "aria-errormessage": PropTypes.string,
  "aria-expanded": PropTypes.oneOfType([PropTypes.oneOf(["false", "true"]), PropTypes.bool]),
  "aria-flowto": PropTypes.string,
  "aria-grabbed": PropTypes.oneOfType([PropTypes.oneOf(["false", "true"]), PropTypes.bool]),
  "aria-haspopup": PropTypes.oneOfType([PropTypes.oneOf(["dialog", "false", "grid", "listbox", "menu", "tree", "true"]), PropTypes.bool]),
  "aria-hidden": PropTypes.oneOfType([PropTypes.oneOf(["false", "true"]), PropTypes.bool]),
  "aria-invalid": PropTypes.oneOfType([PropTypes.oneOf(["false", "grammar", "spelling", "true"]), PropTypes.bool]),
  "aria-keyshortcuts": PropTypes.string,
  "aria-label": PropTypes.string,
  "aria-labelledby": PropTypes.string,
  "aria-level": PropTypes.number,
  "aria-live": PropTypes.oneOf(["assertive", "off", "polite"]),
  "aria-modal": PropTypes.oneOfType([PropTypes.oneOf(["false", "true"]), PropTypes.bool]),
  "aria-multiline": PropTypes.oneOfType([PropTypes.oneOf(["false", "true"]), PropTypes.bool]),
  "aria-multiselectable": PropTypes.oneOfType([PropTypes.oneOf(["false", "true"]), PropTypes.bool]),
  "aria-orientation": PropTypes.oneOf(["horizontal", "vertical"]),
  "aria-owns": PropTypes.string,
  "aria-placeholder": PropTypes.string,
  "aria-posinset": PropTypes.number,
  "aria-pressed": PropTypes.oneOfType([PropTypes.oneOf(["false", "mixed", "true"]), PropTypes.bool]),
  "aria-readonly": PropTypes.oneOfType([PropTypes.oneOf(["false", "true"]), PropTypes.bool]),
  "aria-relevant": PropTypes.oneOf(["additions removals", "additions text", "additions", "all", "removals additions", "removals text", "removals", "text additions", "text removals", "text"]),
  "aria-required": PropTypes.oneOfType([PropTypes.oneOf(["false", "true"]), PropTypes.bool]),
  "aria-roledescription": PropTypes.string,
  "aria-rowcount": PropTypes.number,
  "aria-rowindex": PropTypes.number,
  "aria-rowspan": PropTypes.number,
  "aria-selected": PropTypes.oneOfType([PropTypes.oneOf(["false", "true"]), PropTypes.bool]),
  "aria-setsize": PropTypes.number,
  "aria-sort": PropTypes.oneOf(["ascending", "descending", "none", "other"]),
  "aria-valuemax": PropTypes.number,
  "aria-valuemin": PropTypes.number,
  "aria-valuenow": PropTypes.number,
  "aria-valuetext": PropTypes.string,
  "ariaDescribedBy": PropTypes.string,
  "ariaLabelledBy": PropTypes.string,
  "autoCapitalize": PropTypes.string,
  "autoComplete": PropTypes.string,
  "autoCorrect": PropTypes.string,
  "autoFocus": PropTypes.bool,
  "autoSave": PropTypes.string,
  "capture": PropTypes.oneOfType([PropTypes.oneOf(["environment", "user"]), PropTypes.bool]),
  "checked": PropTypes.bool,
  "children": PropTypes.node,
  "className": PropTypes.string,
  "color": PropTypes.string,
  "content": PropTypes.string,
  "contentEditable": PropTypes.oneOfType([PropTypes.oneOf(["false", "inherit", "true"]), PropTypes.bool]),
  "contextMenu": PropTypes.string,
  "dangerouslySetInnerHTML": PropTypes.shape({
    "__html": PropTypes.oneOfType([PropTypes.object, PropTypes.string]).isRequired
  }),
  "datatype": PropTypes.string,
  "defaultChecked": PropTypes.bool,
  "defaultValue": PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.number, PropTypes.string]),
  "dir": PropTypes.string,
  "disabled": PropTypes.bool,
  "draggable": PropTypes.oneOfType([PropTypes.oneOf(["false", "true"]), PropTypes.bool]),
  "enterKeyHint": PropTypes.oneOf(["done", "enter", "go", "next", "previous", "search", "send"]),
  "form": PropTypes.string,
  "formAction": PropTypes.string,
  "formEncType": PropTypes.string,
  "formMethod": PropTypes.string,
  "formNoValidate": PropTypes.bool,
  "formTarget": PropTypes.string,
  "height": PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  "hidden": PropTypes.bool,
  "id": PropTypes.string,
  "inlist": PropTypes.any,
  "inputMode": PropTypes.oneOf(["decimal", "email", "none", "numeric", "search", "tel", "text", "url"]),
  "is": PropTypes.string,
  "itemID": PropTypes.string,
  "itemProp": PropTypes.string,
  "itemRef": PropTypes.string,
  "itemScope": PropTypes.bool,
  "itemType": PropTypes.string,
  "lang": PropTypes.string,
  "list": PropTypes.string,
  "max": PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  "maxLength": PropTypes.number,
  "min": PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  "minLength": PropTypes.number,
  "multiple": PropTypes.bool,
  "name": PropTypes.string,
  "nonce": PropTypes.string,
  "onAbort": PropTypes.func,
  "onAbortCapture": PropTypes.func,
  "onAnimationEnd": PropTypes.func,
  "onAnimationEndCapture": PropTypes.func,
  "onAnimationIteration": PropTypes.func,
  "onAnimationIterationCapture": PropTypes.func,
  "onAnimationStart": PropTypes.func,
  "onAnimationStartCapture": PropTypes.func,
  "onAuxClick": PropTypes.func,
  "onAuxClickCapture": PropTypes.func,
  "onBeforeInput": PropTypes.func,
  "onBeforeInputCapture": PropTypes.func,
  "onBlur": PropTypes.func,
  "onBlurCapture": PropTypes.func,
  "onCanPlay": PropTypes.func,
  "onCanPlayCapture": PropTypes.func,
  "onCanPlayThrough": PropTypes.func,
  "onCanPlayThroughCapture": PropTypes.func,
  "onChange": PropTypes.func,
  "onChangeCapture": PropTypes.func,
  "onClick": PropTypes.func,
  "onClickCapture": PropTypes.func,
  "onCompositionEnd": PropTypes.func,
  "onCompositionEndCapture": PropTypes.func,
  "onCompositionStart": PropTypes.func,
  "onCompositionStartCapture": PropTypes.func,
  "onCompositionUpdate": PropTypes.func,
  "onCompositionUpdateCapture": PropTypes.func,
  "onContextMenu": PropTypes.func,
  "onContextMenuCapture": PropTypes.func,
  "onCopy": PropTypes.func,
  "onCopyCapture": PropTypes.func,
  "onCut": PropTypes.func,
  "onCutCapture": PropTypes.func,
  "onDoubleClick": PropTypes.func,
  "onDoubleClickCapture": PropTypes.func,
  "onDrag": PropTypes.func,
  "onDragCapture": PropTypes.func,
  "onDragEnd": PropTypes.func,
  "onDragEndCapture": PropTypes.func,
  "onDragEnter": PropTypes.func,
  "onDragEnterCapture": PropTypes.func,
  "onDragExit": PropTypes.func,
  "onDragExitCapture": PropTypes.func,
  "onDragLeave": PropTypes.func,
  "onDragLeaveCapture": PropTypes.func,
  "onDragOver": PropTypes.func,
  "onDragOverCapture": PropTypes.func,
  "onDragStart": PropTypes.func,
  "onDragStartCapture": PropTypes.func,
  "onDrop": PropTypes.func,
  "onDropCapture": PropTypes.func,
  "onDurationChange": PropTypes.func,
  "onDurationChangeCapture": PropTypes.func,
  "onEmptied": PropTypes.func,
  "onEmptiedCapture": PropTypes.func,
  "onEncrypted": PropTypes.func,
  "onEncryptedCapture": PropTypes.func,
  "onEnded": PropTypes.func,
  "onEndedCapture": PropTypes.func,
  "onError": PropTypes.func,
  "onErrorCapture": PropTypes.func,
  "onFocus": PropTypes.func,
  "onFocusCapture": PropTypes.func,
  "onGotPointerCapture": PropTypes.func,
  "onGotPointerCaptureCapture": PropTypes.func,
  "onInput": PropTypes.func,
  "onInputCapture": PropTypes.func,
  "onInvalid": PropTypes.func,
  "onInvalidCapture": PropTypes.func,
  "onKeyDown": PropTypes.func,
  "onKeyDownCapture": PropTypes.func,
  "onKeyPress": PropTypes.func,
  "onKeyPressCapture": PropTypes.func,
  "onKeyUp": PropTypes.func,
  "onKeyUpCapture": PropTypes.func,
  "onLoad": PropTypes.func,
  "onLoadCapture": PropTypes.func,
  "onLoadedData": PropTypes.func,
  "onLoadedDataCapture": PropTypes.func,
  "onLoadedMetadata": PropTypes.func,
  "onLoadedMetadataCapture": PropTypes.func,
  "onLoadStart": PropTypes.func,
  "onLoadStartCapture": PropTypes.func,
  "onLostPointerCapture": PropTypes.func,
  "onLostPointerCaptureCapture": PropTypes.func,
  "onMouseDown": PropTypes.func,
  "onMouseDownCapture": PropTypes.func,
  "onMouseEnter": PropTypes.func,
  "onMouseLeave": PropTypes.func,
  "onMouseMove": PropTypes.func,
  "onMouseMoveCapture": PropTypes.func,
  "onMouseOut": PropTypes.func,
  "onMouseOutCapture": PropTypes.func,
  "onMouseOver": PropTypes.func,
  "onMouseOverCapture": PropTypes.func,
  "onMouseUp": PropTypes.func,
  "onMouseUpCapture": PropTypes.func,
  "onPaste": PropTypes.func,
  "onPasteCapture": PropTypes.func,
  "onPause": PropTypes.func,
  "onPauseCapture": PropTypes.func,
  "onPlay": PropTypes.func,
  "onPlayCapture": PropTypes.func,
  "onPlaying": PropTypes.func,
  "onPlayingCapture": PropTypes.func,
  "onPointerCancel": PropTypes.func,
  "onPointerCancelCapture": PropTypes.func,
  "onPointerDown": PropTypes.func,
  "onPointerDownCapture": PropTypes.func,
  "onPointerEnter": PropTypes.func,
  "onPointerEnterCapture": PropTypes.func,
  "onPointerLeave": PropTypes.func,
  "onPointerLeaveCapture": PropTypes.func,
  "onPointerMove": PropTypes.func,
  "onPointerMoveCapture": PropTypes.func,
  "onPointerOut": PropTypes.func,
  "onPointerOutCapture": PropTypes.func,
  "onPointerOver": PropTypes.func,
  "onPointerOverCapture": PropTypes.func,
  "onPointerUp": PropTypes.func,
  "onPointerUpCapture": PropTypes.func,
  "onProgress": PropTypes.func,
  "onProgressCapture": PropTypes.func,
  "onRateChange": PropTypes.func,
  "onRateChangeCapture": PropTypes.func,
  "onReset": PropTypes.func,
  "onResetCapture": PropTypes.func,
  "onScroll": PropTypes.func,
  "onScrollCapture": PropTypes.func,
  "onSeeked": PropTypes.func,
  "onSeekedCapture": PropTypes.func,
  "onSeeking": PropTypes.func,
  "onSeekingCapture": PropTypes.func,
  "onSelect": PropTypes.func,
  "onSelectCapture": PropTypes.func,
  "onStalled": PropTypes.func,
  "onStalledCapture": PropTypes.func,
  "onSubmit": PropTypes.func,
  "onSubmitCapture": PropTypes.func,
  "onSuspend": PropTypes.func,
  "onSuspendCapture": PropTypes.func,
  "onTimeUpdate": PropTypes.func,
  "onTimeUpdateCapture": PropTypes.func,
  "onTouchCancel": PropTypes.func,
  "onTouchCancelCapture": PropTypes.func,
  "onTouchEnd": PropTypes.func,
  "onTouchEndCapture": PropTypes.func,
  "onTouchMove": PropTypes.func,
  "onTouchMoveCapture": PropTypes.func,
  "onTouchStart": PropTypes.func,
  "onTouchStartCapture": PropTypes.func,
  "onTransitionEnd": PropTypes.func,
  "onTransitionEndCapture": PropTypes.func,
  "onVolumeChange": PropTypes.func,
  "onVolumeChangeCapture": PropTypes.func,
  "onWaiting": PropTypes.func,
  "onWaitingCapture": PropTypes.func,
  "onWheel": PropTypes.func,
  "onWheelCapture": PropTypes.func,
  "pattern": PropTypes.string,
  "placeholder": PropTypes.string,
  "prefix": PropTypes.string,
  "property": PropTypes.string,
  "radioGroup": PropTypes.string,
  "readOnly": PropTypes.bool,
  "rel": PropTypes.string,
  "required": PropTypes.bool,
  "resource": PropTypes.string,
  "results": PropTypes.number,
  "rev": PropTypes.string,
  "role": PropTypes.string,
  "security": PropTypes.string,
  "slot": PropTypes.string,
  "spellCheck": PropTypes.oneOfType([PropTypes.oneOf(["false", "true"]), PropTypes.bool]),
  "src": PropTypes.string,
  "step": PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  "style": PropTypes.object,
  "suppressContentEditableWarning": PropTypes.bool,
  "suppressHydrationWarning": PropTypes.bool,
  "tabIndex": PropTypes.number,
  "title": PropTypes.string,
  "translate": PropTypes.oneOf(["no", "yes"]),
  "type": PropTypes.string.isRequired,
  "typeof": PropTypes.string,
  "unselectable": PropTypes.oneOf(["off", "on"]),
  "validationIconId": PropTypes.string,
  "value": PropTypes.string,
  "vocab": PropTypes.string,
  "width": PropTypes.oneOfType([PropTypes.number, PropTypes.string])
};
HiddenCheckableInput.displayName = "HiddenCheckableInput";
export default /*#__PURE__*/React.memo(HiddenCheckableInput);